import { useEffect, useState } from "react";
import { Plus, MapPin, Trash2, Star, Calendar } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import LocationMap from "../../components/LocationMap";

const empty = {
  nome: "", indirizzo: "", civico: "", citta: "", cap: "", provincia: "",
  nazione: "Italia", lat: null, lng: null,
  telefono: "", email: "", referente: "", attiva: true,
  data_apertura: "", principale: false,
};

export default function Sedi() {
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/sedi").then((r) => {
    // ordina: principale prima, poi nome
    const sorted = [...(r.data || [])].sort((a, b) => {
      if (a.principale && !b.principale) return -1;
      if (!a.principale && b.principale) return 1;
      return (a.nome || "").localeCompare(b.nome || "");
    });
    setList(sorted);
  });
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/sedi/${editId}`, form);
      else await api.post("/sedi", form);
      toast.success("Sede salvata");
      setShow(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error("Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare sede?")) return;
    try { await api.delete(`/sedi/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail); }
  };

  return (
    <div className="space-y-6" data-testid="sedi-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Sedi Operative</h1>
          <p className="text-sm text-white/45 mt-1">{list.length} sedi · gestione multi-location</p>
        </div>
        <button data-testid="sede-add" onClick={() => { setForm(empty); setEditId(null); setShow(true); }} className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2">
          <Plus size={14} /> Nuova Sede
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((s) => (
          <div key={s.id} className={`glass rounded-2xl p-5 ${s.principale ? "border-[#ff0033]/40 shadow-[0_0_30px_-10px_#ff0033]" : ""}`} data-testid={`sede-${s.id}`}>
            <div className="flex items-start justify-between mb-3 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <MapPin size={16} className="text-[#ff0033] shrink-0" />
                <div className="font-display font-bold truncate">{s.nome}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {s.principale && (
                  <span className="text-[9px] uppercase tracking-widest px-2 py-1 rounded-full border border-[#ff0033] bg-[#ff0033]/15 text-[#ff5577] inline-flex items-center gap-1" data-testid={`sede-principale-${s.id}`}>
                    <Star size={9} className="fill-current" /> Principale
                  </span>
                )}
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${s.attiva ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-white/10 text-white/40 border-white/20"}`}>
                  {s.attiva ? "Attiva" : "Off"}
                </span>
              </div>
            </div>
            <div className="text-sm text-white/70 space-y-1">
              <div>{s.indirizzo}{s.civico ? `, ${s.civico}` : ""}</div>
              <div>{s.cap} {s.citta} {s.provincia ? `(${s.provincia})` : ""}</div>
              {s.telefono && <div className="text-xs text-white/40 mt-2">📞 {s.telefono}</div>}
              {s.referente && <div className="text-xs text-white/40">Ref. {s.referente}</div>}
              {s.data_apertura && (
                <div className="text-xs text-white/40 inline-flex items-center gap-1.5 mt-2">
                  <Calendar size={11} /> Aperta dal {new Date(s.data_apertura).toLocaleDateString('it-IT')}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-1 mt-4 pt-3 border-t border-white/5">
              <button onClick={() => { setForm({ ...empty, ...s }); setEditId(s.id); setShow(true); }} className="text-[11px] px-3 py-1 rounded-full bg-white/[0.04] hover:bg-white/10">Modifica</button>
              <button onClick={() => remove(s.id)} className="p-1.5 rounded-full bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="col-span-3 text-center py-10 text-white/40">Nessuna sede. Aggiungi la prima.</div>}
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-2xl glass-strong rounded-3xl p-6 max-h-[92vh] overflow-auto" data-testid="sede-form">
            <div className="font-display text-xl font-bold mb-4">{editId ? "Modifica Sede" : "Nuova Sede"}</div>
            <div className="grid grid-cols-2 gap-3">
              <input required placeholder="Nome sede" data-testid="sede-nome" className="col-span-2 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              <div className="col-span-2">
                <AddressAutocomplete
                  testid="sede-addr"
                  value={form.indirizzo}
                  onChange={(v) => setForm({ ...form, indirizzo: v })}
                  onSelect={(p) => setForm({
                    ...form,
                    indirizzo: p.via || p.indirizzo,
                    civico: p.civico || form.civico,
                    citta: p.citta || form.citta,
                    cap: p.cap || form.cap,
                    provincia: p.provincia || form.provincia,
                    nazione: p.nazione || form.nazione,
                    lat: p.lat, lng: p.lng,
                  })}
                  placeholder="Via / strada · inizia a scrivere…"
                  required
                />
              </div>
              <input required placeholder="Città" data-testid="sede-citta" className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} />
              <input placeholder="N° civico" data-testid="sede-civico" className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" value={form.civico} onChange={(e) => setForm({ ...form, civico: e.target.value })} />
              <input placeholder="CAP" data-testid="sede-cap" className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} />
              <input placeholder="Provincia" className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value.toUpperCase().slice(0, 4) })} />
              <input placeholder="Nazione" className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" value={form.nazione} onChange={(e) => setForm({ ...form, nazione: e.target.value })} />
              <div className="col-span-1">
                <label className="text-[10px] uppercase tracking-widest text-white/40">Data inizio attività sede</label>
                <input type="date" data-testid="sede-data-apertura" className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" value={form.data_apertura || ""} onChange={(e) => setForm({ ...form, data_apertura: e.target.value })} />
              </div>
              <input placeholder="Telefono" className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
              <input placeholder="Email" className="col-span-2 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input placeholder="Referente" className="col-span-2 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" value={form.referente} onChange={(e) => setForm({ ...form, referente: e.target.value })} />
              <label className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5">
                <input type="checkbox" checked={form.attiva} onChange={(e) => setForm({ ...form, attiva: e.target.checked })} className="accent-[#ff0033]" />
                Sede attiva
              </label>
              <label className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border cursor-pointer transition-all ${form.principale ? "bg-[#ff0033]/10 border-[#ff0033]/40 text-[#ff5577]" : "bg-white/[0.02] border-white/5 hover:border-white/15"}`} data-testid="sede-principale-toggle">
                <input type="checkbox" checked={!!form.principale} onChange={(e) => setForm({ ...form, principale: e.target.checked })} className="accent-[#ff0033]" />
                <Star size={12} className={form.principale ? "fill-current" : ""} />
                Sede Principale
              </label>
              {(form.lat && form.lng) && (
                <div className="col-span-2">
                  <LocationMap lat={form.lat} lng={form.lng} label={`${form.indirizzo}${form.civico ? `, ${form.civico}` : ""}${form.citta ? `, ${form.citta}` : ""}`} height={200} testid="sede-map" />
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
              <button data-testid="sede-form-submit" type="submit" className="btn-neon px-6 py-2.5 rounded-full text-sm">Salva</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
